import { LEGAL_DOCUMENTS, PREFERRED_NAME_MAX_LENGTH, formatPhone } from '@hue/contracts';
import {
  Button,
  Card,
  CardHeader,
  FormErrorSummary,
  Input,
  Modal,
  PhoneInput,
  SegmentedControl,
  StatusPill,
  Switch,
  useSnackbar,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Download,
  FileText,
  Mail,
  MessageSquare,
  ShieldCheck,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { accountApi, type CustomerAccount, type CustomerAccountView } from '../lib/account-api.js';
import { getApiError } from '../lib/api.js';
import { sessionQuery } from '../lib/session.js';
import { formatDate, formatDateTime } from '../lib/time-zone.js';
import { CustomerAppointments } from './customer-appointments.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { PRIVACY_OFFICER_CONTACT } from './legal-documents.js';
import { PortalFooter, PortalMasthead } from './portal-masthead.js';

export type CustomerAccountTab = 'appointments' | 'profile' | 'privacy';

/// A área do cliente (ADR 0067): os pedidos em todas as empresas, os dados da conta com as
/// mensagens de cada empresa e os direitos de privacidade.
export function CustomerAccountScreen({
  tab,
  onTab,
}: {
  tab: CustomerAccountTab;
  onTab: (tab: CustomerAccountTab) => void;
}) {
  const account = useQuery({
    queryKey: ['customer', 'account'],
    queryFn: accountApi.get,
    retry: false,
  });
  const name = account.data
    ? account.data.account.preferredName || account.data.account.firstName
    : '';
  return (
    <div className="public-portal min-h-screen bg-surface text-content">
      <PortalMasthead />
      <main className="mx-auto px-5 pb-20 pt-8 sm:px-8 sm:pt-12">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {name ? `Olá, ${name}` : 'Minha conta'}
        </h1>
        <p className="mt-2 text-sm text-content-muted">
          Sua área de cliente, válida em todas as empresas do portal.
        </p>
        <SegmentedControl
          tabs
          className="mb-6 mt-6"
          label="Seções da minha conta"
          value={tab}
          onChange={onTab}
          options={[
            { value: 'appointments', label: 'Meus agendamentos' },
            { value: 'profile', label: 'Meus dados e mensagens' },
            { value: 'privacy', label: 'Privacidade' },
          ]}
        />
        {account.isPending ? (
          <LoadingCard label="Carregando sua conta" />
        ) : account.isError ? (
          <ErrorCard message="Não foi possível carregar sua conta." />
        ) : tab === 'appointments' ? (
          <CustomerAppointments companies={account.data.companies} />
        ) : tab === 'profile' ? (
          <ProfileTab view={account.data} />
        ) : (
          <PrivacyTab view={account.data} />
        )}
      </main>
      <PortalFooter />
    </div>
  );
}

function ProfileTab({ view }: { view: CustomerAccountView }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const { account } = view;
  const [firstName, setFirstName] = useState(account.firstName);
  const [lastName, setLastName] = useState(account.lastName);
  const [preferredName, setPreferredName] = useState(account.preferredName ?? '');
  const [phone, setPhone] = useState(formatPhone(account.phone) ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [changingEmail, setChangingEmail] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      accountApi.update({
        firstName,
        lastName,
        preferredName: preferredName.trim() || undefined,
        phone: phone.trim() || undefined,
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(['customer', 'account'], next);
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      snackbar.success('Dados salvos.');
    },
  });
  const marketing = useMutation({
    mutationFn: (input: { key: string; granted: boolean }) =>
      accountApi.setMarketing(input.key, input.granted),
    onSuccess: (next, input) => {
      queryClient.setQueryData(['customer', 'account'], next);
      const company = next.companies.find((item) => item.key === input.key)?.displayName ?? '';
      snackbar.success(
        input.granted ? `Novidades de ${company} ligadas.` : `Novidades de ${company} desligadas.`,
      );
    },
    onError: (cause) => snackbar.failure(getApiError(cause)),
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (firstName.trim().length < 2) found['account-first-name'] = 'Informe seu nome.';
    if (lastName.trim().length < 2) found['account-last-name'] = 'Informe seu sobrenome.';
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  }
  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="Meus dados" icon={<UserRound className="size-4" />} />
        <form noValidate onSubmit={submit} className="max-w-2xl space-y-5">
          <p className="text-xs text-content-muted">
            Todos os campos são obrigatórios, exceto os marcados como opcionais.
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              id="account-first-name"
              label="Nome"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              maxLength={120}
              error={errors['account-first-name']}
            />
            <Input
              id="account-last-name"
              label="Sobrenome"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              maxLength={120}
              error={errors['account-last-name']}
            />
          </div>
          <Input
            id="account-preferred-name"
            label="Como você gostaria de ser chamado(a)? (opcional)"
            value={preferredName}
            onChange={(event) => setPreferredName(event.target.value)}
            maxLength={PREFERRED_NAME_MAX_LENGTH}
            placeholder={firstName}
            hint="Em branco, usamos o seu Nome."
          />
          <Input
            id="account-email"
            label="E-mail"
            value={account.email}
            readOnly
            hint="Para trocar, confirmamos o novo endereço antes de usá-lo."
            action={
              <Button type="button" variant="outline" onClick={() => setChangingEmail(true)}>
                Trocar e-mail
              </Button>
            }
          />
          <div className="space-y-2">
            <PhoneInput
              id="account-phone"
              label="Celular (opcional, recomendado)"
              value={phone}
              onValueChange={setPhone}
            />
            <p className="text-xs leading-5 text-content-muted">
              As empresas em que você já pediu horário passam a ver os dados novos.
            </p>
          </div>
          {save.error ? (
            <p
              role="alert"
              className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
            >
              {getApiError(save.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-4">
            <Button type="submit" loading={save.isPending}>
              Salvar dados
            </Button>
            <FormErrorSummary />
          </div>
        </form>
      </Card>
      <Card>
        <CardHeader title="Mensagens" icon={<MessageSquare className="size-4" />} />
        <div className="space-y-5">
          <div className="space-y-2 border-b border-surface-border pb-5">
            <Switch checked disabled onChange={() => undefined}>
              Sobre seus pedidos
            </Switch>
            <p className="text-xs leading-5 text-content-muted">
              Confirmação, remarcação, cancelamento e pagamento, por e-mail. Ficam sempre ligadas:
              sem elas o agendamento não funciona.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Novidades e promoções</p>
            <p className="mt-1 text-xs leading-5 text-content-muted">
              Cada empresa pergunta à parte. Começam desligadas e você muda quando quiser.
            </p>
          </div>
          {view.companies.map((company) => (
            <div key={company.key} className="space-y-1">
              <Switch
                checked={company.marketing}
                disabled={marketing.isPending}
                onChange={(granted) => marketing.mutate({ key: company.key, granted })}
              >
                {company.displayName}
              </Switch>
              <p className="pl-14 text-xs text-content-faint">
                Cliente desde {formatDate(company.since, 'America/Sao_Paulo')}
              </p>
            </div>
          ))}
          <p className="text-xs text-content-muted">
            Uma empresa aparece aqui depois do seu primeiro pedido nela.
          </p>
        </div>
      </Card>
      <EmailChangeDialog open={changingEmail} onOpenChange={setChangingEmail} />
    </div>
  );
}

function EmailChangeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const request = useMutation({
    mutationFn: () => accountApi.requestEmailChange({ email, password }),
    onSuccess: (result) => {
      onOpenChange(false);
      snackbar.success(`Enviamos o link de confirmação para ${email.trim().toLowerCase()}.`);
      if (result.developmentToken)
        void navigate({ to: '/confirm-email', search: { token: result.developmentToken } });
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim()))
      found['email-change-email'] = 'Informe um e-mail válido.';
    if (!password) found['email-change-password'] = 'Informe sua senha.';
    setErrors(found);
    if (Object.keys(found).length === 0) request.mutate();
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Trocar e-mail"
      description="O novo endereço só passa a valer depois que você abrir o link que enviamos para ele."
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <FormErrorSummary form="email-change-form" />
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="email-change-form" loading={request.isPending}>
            Enviar confirmação
          </Button>
        </div>
      }
    >
      <form id="email-change-form" noValidate onSubmit={submit} className="space-y-4">
        <Input
          id="email-change-email"
          label="Novo e-mail"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          leftIcon={<Mail className="size-4" />}
          error={errors['email-change-email']}
        />
        <Input
          id="email-change-password"
          label="Sua senha"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={errors['email-change-password']}
        />
        {request.error ? (
          <p role="alert" className="text-sm text-danger">
            {getApiError(request.error)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

function PrivacyTab({ view }: { view: CustomerAccountView }) {
  const snackbar = useSnackbar();
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  async function download() {
    setExporting(true);
    try {
      const file = await accountApi.export();
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'meus-dados.json';
      link.click();
      URL.revokeObjectURL(url);
      snackbar.success('Cópia dos seus dados baixada.');
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    } finally {
      setExporting(false);
    }
  }
  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="Documentos aceitos" icon={<FileText className="size-4" />} />
        <ul className="space-y-3 text-sm">
          {(['TERMS', 'PRIVACY'] as const).map((document) => {
            const accepted = view.account.acceptances.find((item) => item.document === document);
            const current = accepted?.version === LEGAL_DOCUMENTS[document].version;
            return (
              <li key={document} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <Link
                    to={document === 'TERMS' ? '/terms' : '/privacy'}
                    className="font-semibold underline underline-offset-2"
                  >
                    {LEGAL_DOCUMENTS[document].label}
                  </Link>
                  <span className="block text-xs text-content-muted">
                    {accepted
                      ? `Versão ${accepted.version.split('-').reverse().join('/')} · aceita em ${formatDateTime(accepted.acceptedAt, 'America/Sao_Paulo')}`
                      : 'Ainda não aceito'}
                  </span>
                </span>
                <StatusPill tone={current ? 'positive' : 'warning'}>
                  {current ? 'Versão atual' : 'Aceite pendente'}
                </StatusPill>
              </li>
            );
          })}
        </ul>
      </Card>
      <Card>
        <CardHeader title="Quem tem seus dados" icon={<ShieldCheck className="size-4" />} />
        <p className="max-w-prose text-sm leading-6 text-content-muted">
          A plataforma guarda sua conta. Cada empresa abaixo guarda o cadastro de cliente que nasceu
          no seu primeiro pedido e responde por ele.
        </p>
        <ul className="mt-4 space-y-3 text-sm">
          {view.companies.length === 0 ? (
            <li className="text-content-muted">Nenhuma empresa ainda.</li>
          ) : (
            view.companies.map((company) => (
              <li key={company.key} className="border-t border-surface-border pt-3">
                <strong>{company.displayName}</strong>
                <span className="block text-xs text-content-muted">
                  Desde {formatDate(company.since, 'America/Sao_Paulo')} · nome, e-mail
                  {view.account.phone ? ', celular' : ''} e seus pedidos nela
                </span>
              </li>
            ))
          )}
        </ul>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader title="Seus direitos" icon={<UserRound className="size-4" />} />
        <p className="max-w-prose text-sm leading-6 text-content-muted">
          Você pode ver e corrigir seus dados em Meus dados, receber uma cópia deles e excluir a
          conta. Pedidos sobre o cadastro de uma empresa vão para ela, pelos contatos da página
          dela.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="h-auto min-h-11 max-w-full py-2"
            loading={exporting}
            onClick={() => void download()}
          >
            <Download className="size-4 shrink-0" />
            <span className="min-w-0 whitespace-normal">Baixar cópia dos meus dados</span>
          </Button>
          <Button variant="ghost" onClick={() => setDeleting(true)}>
            <Trash2 className="size-4" /> Excluir minha conta
          </Button>
        </div>
        <p className="mt-5 text-xs text-content-muted">
          Encarregado de dados da plataforma: {PRIVACY_OFFICER_CONTACT}
        </p>
      </Card>
      <DeleteAccountDialog account={view.account} open={deleting} onOpenChange={setDeleting} />
    </div>
  );
}

function DeleteAccountDialog({
  account,
  open,
  onOpenChange,
}: {
  account: CustomerAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const remove = useMutation({
    mutationFn: () => accountApi.delete(password),
    onSuccess: async () => {
      queryClient.setQueryData(sessionQuery.queryKey, null);
      queryClient.removeQueries({ queryKey: ['customer'] });
      snackbar.success('Conta excluída.');
      await navigate({ to: '/', replace: true });
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password) {
      setError('Informe sua senha.');
      return;
    }
    setError(undefined);
    remove.mutate();
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Excluir sua conta?"
      description="Esta ação não pode ser desfeita."
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Manter conta
          </Button>
          <Button type="submit" form="delete-account-form" loading={remove.isPending}>
            Excluir definitivamente
          </Button>
        </div>
      }
    >
      <form
        id="delete-account-form"
        noValidate
        onSubmit={submit}
        className="space-y-4 text-sm leading-6"
      >
        <p>
          Apagamos seus dados da conta — nome, e-mail, celular, senha e preferências — e você não
          entra mais no portal. O cadastro que cada empresa mantém sobre você segue as regras dela,
          como a guarda fiscal das vendas; para pedir a exclusão lá, fale com a empresa.
        </p>
        <p className="text-content-muted">
          {account.staffAccess
            ? 'Você tem acesso a uma empresa ou à plataforma: peça para retirarem esse acesso antes.'
            : 'Se tiver agendamentos por acontecer, cancele-os antes.'}
        </p>
        <Input
          id="delete-account-password"
          label="Sua senha"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={error}
        />
        {remove.error ? (
          <p role="alert" className="text-danger">
            {getApiError(remove.error)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
